import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getErrorMessage } from '@/lib/api-errors'
import { toast } from '@/lib/toast'

import {
  deactivateSite,
  getSiteDetail,
  reactivateSite,
  resetSiteFieldData,
  updateSite,
} from '../api/siteApi'

import type { SiteDetailParams, SiteUpdateRequest } from '../api/siteApi'

export function useSiteDetail(siteId: string, params?: SiteDetailParams) {
  return useQuery({
    queryKey: ['site-detail', siteId, params],
    queryFn: () => getSiteDetail(siteId, params),
    enabled: !!siteId,
  })
}

// Broad-prefix invalidation. Any change to a site can ripple into pickers
// (Schedule Survey, Allocations, HOD inbox) and aggregate widgets (dashboard,
// entity stats). Listing all the prefixes here is verbose but cheap, and
// removes the entire class of "stale picker after create/edit" bug — the
// cost is a few extra refetches the user already wanted.
function invalidateSiteCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  siteId: string,
  entityId?: string,
) {
  queryClient.invalidateQueries({ queryKey: ['site-detail', siteId] })
  if (entityId) {
    queryClient.invalidateQueries({ queryKey: ['entity-detail', entityId] })
    queryClient.invalidateQueries({ queryKey: ['entity-sites', entityId] })
    queryClient.invalidateQueries({ queryKey: ['entity-sites-schedule', entityId] })
  } else {
    queryClient.invalidateQueries({ queryKey: ['entity-detail'] })
    queryClient.invalidateQueries({ queryKey: ['entity-sites'] })
    queryClient.invalidateQueries({ queryKey: ['entity-sites-schedule'] })
  }
  queryClient.invalidateQueries({ queryKey: ['entities'] })
  queryClient.invalidateQueries({ queryKey: ['entity-stats'] })
  queryClient.invalidateQueries({ queryKey: ['allocations'] })
  queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  queryClient.invalidateQueries({ queryKey: ['hod-sites'] })
  queryClient.invalidateQueries({ queryKey: ['hod-site-detail', siteId] })
  queryClient.invalidateQueries({ queryKey: ['tasks'] })
}

export function useUpdateSite(siteId: string, entityId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: SiteUpdateRequest) => updateSite(siteId, patch),
    onSuccess: () => {
      invalidateSiteCaches(queryClient, siteId, entityId)
      toast.success('Site updated successfully.')
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update site.')),
  })
}

export function useDeactivateSite(siteId: string, entityId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deactivateSite(siteId),
    onSuccess: () => {
      invalidateSiteCaches(queryClient, siteId, entityId)
      toast.success('Site deactivated successfully.')
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to deactivate site.')),
  })
}

// Clears the FO-captured location + building photo so the officer can
// re-capture on the next visit. Success copy mentions the GIS lag because
// the map dashboard is TTL-cached server-side — the old pin can linger a
// few minutes even though site detail is live.
export function useResetSiteFieldData(siteId: string, entityId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => resetSiteFieldData(siteId),
    onSuccess: () => {
      invalidateSiteCaches(queryClient, siteId, entityId)
      queryClient.invalidateQueries({ queryKey: ['gis'] })
      toast.success(
        'Location & photo cleared. The GIS map may show the old pin for a few minutes.',
      )
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to reset site field data.')),
  })
}

export function useReactivateSite(siteId: string, entityId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => reactivateSite(siteId),
    onSuccess: () => {
      invalidateSiteCaches(queryClient, siteId, entityId)
      toast.success('Site reactivated.')
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to reactivate site.')),
  })
}
